/**
 * Project assets (plan backgrounds, survey photos): raw bytes saved in the .tablo file, and decoded images
 * for the canvas. Assets live outside the undo history; unreferenced ones are dropped on save.
 */
import { useCallback, useRef, useState } from 'react';
import { assetRefs, type Project } from '@elec/domain';
import { parsePlan, VECTOR_PLAN_MIME, type VectorPlan } from '@elec/plan';

export interface AssetStore {
  bytes: ReadonlyMap<string, Uint8Array>;
  /** Decoded backgrounds for the canvas: images, or vector plans (DXF imports). */
  images: ReadonlyMap<string, ImageBitmap | VectorPlan>;
  /** `draw`: decode it for the canvas (plan backgrounds); photos are only shown as files. */
  add(id: string, bytes: Uint8Array, mime: string, draw?: boolean): Promise<void>;
  replaceAll(assets: Record<string, Uint8Array>, project: Project): Promise<void>;
  /** Assets referenced by the project, for saving and exporting. */
  forProject(project: Project): Record<string, Uint8Array>;
}

const mimeOf = (project: Project, id: string) =>
  assetRefs(project).find((r) => r.id === id)?.mime ?? 'image/png';

async function decode(bytes: Uint8Array, mime: string): Promise<ImageBitmap | VectorPlan | null> {
  if (mime === VECTOR_PLAN_MIME) return parsePlan(bytes);
  try {
    return await createImageBitmap(new Blob([bytes.slice().buffer as ArrayBuffer], { type: mime }));
  } catch {
    return null;
  }
}

export function useAssets(): AssetStore {
  const bytes = useRef(new Map<string, Uint8Array>());
  const [images, setImages] = useState<ReadonlyMap<string, ImageBitmap | VectorPlan>>(new Map());

  const add = useCallback(async (id: string, b: Uint8Array, mime: string, draw = true) => {
    bytes.current.set(id, b);
    if (!draw) return;
    const img = await decode(b, mime);
    if (img) setImages((m) => new Map(m).set(id, img));
  }, []);

  const replaceAll = useCallback(async (assets: Record<string, Uint8Array>, project: Project) => {
    // Refill the same map: holders of this store (the cloud uploader) keep seeing the files.
    bytes.current.clear();
    for (const [id, b] of Object.entries(assets)) bytes.current.set(id, b);
    const next = new Map<string, ImageBitmap | VectorPlan>();
    const drawn = new Set(Object.values(project.sheets).map((sh) => sh.background?.assetId));
    for (const [id, b] of Object.entries(assets)) {
      if (!drawn.has(id)) continue;
      const img = await decode(b, mimeOf(project, id));
      if (img) next.set(id, img);
    }
    setImages(next);
  }, []);

  const forProject = useCallback((project: Project) => {
    const out: Record<string, Uint8Array> = {};
    for (const { id } of assetRefs(project)) {
      const b = bytes.current.get(id);
      if (b) out[id] = b;
    }
    return out;
  }, []);

  return { bytes: bytes.current, images, add, replaceAll, forProject };
}
